/* eslint-disable react/prefer-stateless-function */
// Third-party
import React from 'react';
import { connect } from 'react-redux';

// Components
import { SearchInput } from '../../components/inputs';

// Utils
import { FormattedMessage, FormattedHTMLMessage, formatMessage } from '../../../utils/i18n';
import { IconButton, PrimaryButton } from '../../components/buttons';
import DataTable from '../../components/data/DataTable';
import { GenericEmptyState } from '../../components/misc';
import AddDelegateModal from '../../components/modals/AddDelegateModal';
import { ReduxState } from '../../../redux/modules/types';
import { getMyDelegates, removeDelegate, addDelegate } from '../../../redux/modules/delegates';
import { PERMISSIONS, userHasPermission } from '../../../utils/permissions';
import { searchObjectsOnFields } from '../../../utils/search';
import Delegate from '@/types/delegate';
import Locale from '@/types/locale';
import User from '@/types/user';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Stack } from '@capintel/component-lib/stack';

type DispatchProps = {
  getMyDelegates: () => Promise<Array<Delegate>>;
  removeDelegate: (id: string) => Promise<Array<Delegate>>;
  addDelegate: (email: string, fname: string, lname: string, locale: Locale) => Promise<Array<Delegate>>;
};

type State = {
  showAddDelegateModal: boolean;
  showDeleteDelegateModal: boolean;
  delegateToDelete: string;
  query: string;
};

type StateProps = {
  locale: Locale;
  delegates: Array<Delegate>;
};

type Props = StateProps &
  DispatchProps & {
    user: User;
  };

class SettingsDelegates extends React.Component<Props, State> {
  readonly state: State = {
    showAddDelegateModal: false,
    showDeleteDelegateModal: false,
    delegateToDelete: '',
    query: '',
  };

  componentDidMount() {
    this.props.getMyDelegates();
  }

  filterFromQuery = (objs: Delegate[], query: string) => {
    if (!query) {
      return objs;
    }

    const searchableFields = ['name', 'email'];

    return searchObjectsOnFields(query, objs, searchableFields);
  };

  onSearch = (query: string) => {
    this.setState({ query });
  };

  showAddDelegateModal = () => {
    this.setState({ showAddDelegateModal: true });
  };

  showDeleteDelegateModal = (delegateToDelete: string) => {
    this.setState(() => ({
      showDeleteDelegateModal: true,
      delegateToDelete,
    }));
  };

  hideAddDelegateModal = () => {
    this.setState({ showAddDelegateModal: false });
  };

  hideDeleteDelegateModal = () => {
    this.setState({ showDeleteDelegateModal: false });
  };

  onAddDelegate = (fname: string, lname: string, email: string) => {
    const { locale } = this.props;
    return this.props.addDelegate(email, fname, lname, locale);
  };

  removeSelectedDelegate = () => {
    const { delegateToDelete } = this.state;
    this.props.removeDelegate(delegateToDelete).then(() => {
      this.setState(() => ({ delegateToDelete: '' }));
      this.hideDeleteDelegateModal();
    });
  };

  isAddDisabled = () => {
    const { delegates, user } = this.props;
    if (user == null) {
      return true;
    }

    const delegateLimit = user.authUser.rateLimits.DELEGATES?.limit ?? Number.MAX_VALUE;
    return delegates == null || delegateLimit <= delegates.length;
  };

  getColumns = () => {
    const columns = [
      {
        Header: formatMessage('Settings.Delegates.Fields.Name'),
        accessor: 'name',
        className: 'enterprise-member-name',
        width: 250,
      },
      {
        Header: formatMessage('Settings.Delegates.Fields.Email'),
        accessor: 'email',
        className: 'enterprise-member-name',
      },
      {
        accessor: 'id',
        className: 'cell-icon',
        Cell: ({ value }: { value: any }) => (
          <IconButton
            icon="ds-delete"
            onClick={() => {
              this.showDeleteDelegateModal(value);
            }}
          />
        ),
        width: 48,
      },
    ];
    return columns;
  };

  render() {
    const { user, delegates } = this.props;
    if (user == null || user.authUser.delegateAccess == null || !userHasPermission(this.props.user, PERMISSIONS.DELEGATE_MANAGEMENT)) {
      return null;
    }

    const { showAddDelegateModal, showDeleteDelegateModal, query, delegateToDelete } = this.state;

    let delegateName = delegateToDelete;
    let delegateEmail = '';
    const delegateObj = delegates != null ? delegates.find(d => d.id === delegateToDelete) : undefined;

    if (delegateObj != null) {
      delegateName = delegateObj.name;
      delegateEmail = `(${delegateObj.email})`;
    }

    const delegateCount = delegates && delegates.length ? delegates.length : 0;

    const filteredDelegates = this.filterFromQuery(delegates, query);
    return (
      <div className="settings-delegates settings-tab">
        <div className="settings-delegates-header">
          <div className="delegates-title">
            <h3>
              <FormattedMessage id="Settings.Delegates.Title" />
            </h3>

            {user.authUser.rateLimits.DELEGATES != null && (
              <p className="delegates-seats">
                <FormattedHTMLMessage
                  id="Settings.Delegates.Seats"
                  values={{
                    used: delegateCount,
                    total: user.authUser.rateLimits.DELEGATES.limit,
                  }}
                />
              </p>
            )}
          </div>

          <div className="delegates-actions">
            <SearchInput
              className="settings-search"
              value={this.state.query}
              onChange={this.onSearch}
              placeholder={formatMessage('Settings.Delegates.SearchDelegates')}
            />

            <PrimaryButton onClick={this.showAddDelegateModal} disabled={this.isAddDisabled()}>
              <i className="icon-plus" />
              <FormattedMessage id="Settings.Delegates.AddDelegate" />
            </PrimaryButton>
          </div>
        </div>

        {delegateCount > 0 && delegates != null && <DataTable data={filteredDelegates} className="results-table flex-grow-1" columns={this.getColumns()} />}

        {delegateCount === 0 && <GenericEmptyState header={formatMessage('Settings.Delegates.NoDelegates.Title')} />}

        <AddDelegateModal show={showAddDelegateModal} onCancel={this.hideAddDelegateModal} onSave={this.onAddDelegate} />

        <DestructiveModal
          open={showDeleteDelegateModal}
          onOpenChange={open => !open && this.hideDeleteDelegateModal()}
          title={formatMessage('Settings.Delegates.DeleteDelegateModal.Title')}
          content={
            <Stack gap="150">
              <FormattedMessage id="Settings.Delegates.DeleteDelegateModal.Body" />
              <ul>
                <li key={delegateName}>{`${delegateName} ${delegateEmail}`}</li>
              </ul>
              <FormattedMessage id="Settings.Delegates.DeleteDelegateModal.Confirmation" />
            </Stack>
          }
          confirmButtonText={formatMessage('Modal.Default.Confirm')}
          cancelButtonText={formatMessage('Common.Cancel')}
          onConfirm={this.removeSelectedDelegate}
          onCancel={this.hideDeleteDelegateModal}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  delegates: state.delegates.delegates,
  locale: state.intl.locale,
});

export default connect(mapStateToProps, {
  getMyDelegates,
  removeDelegate,
  addDelegate,
})(SettingsDelegates);
